import { FetchNextPageOptions, InfiniteQueryObserverResult } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponseModel, useIsFeatureSupported } from '@steelbuy/data-access';
import { ListingBuyerSearchResultModel, PackageBuyerModel, Feature } from '@steelbuy/ts-shared';
import { SearchResults } from '@steelbuy/ui-domain';
import { Separator, SeparatorIdentifier, LoadingSpinner } from '@steelbuy/ui-primitive';
import { ListingAlertNotification } from './ListingAlertNotification';
import { ListingSearchSummary } from './ListingSearchSummary';
import { SearchResultsEmptyState } from './SearchResultsEmptyState';
import { SearchResultsPageHeader } from './SearchResultsPageHeader';
import { useBuyNowAndAddToCart } from '../../hooks/useBuyNowAndAddToCart';
import { createRouteUrl, RoutePath } from '../../router/Routes';
import { TableLayout } from '../../views/layouts/table-layout/TableLayout';
import { getScrollTopMainLayout, scrollMainLayout } from '../app-layout/app-main-layout/AppMainLayout';
import { useSearchFormDataContext } from '../listing-search/SearchFormDataContext';
import { useSearchResultsContext } from '../listing-search/SearchResultsContext';

type SearchResultsLayoutProps<T> = {
    isLoading: boolean;
    isPackage?: boolean;
    data?: ReadonlyArray<T>;
    hasNextPage?: boolean;
    total?: number;
    isFetchingNextPage: boolean;
    fetchNextPage: (
        options?: FetchNextPageOptions | undefined
    ) => Promise<InfiniteQueryObserverResult<ResponseModel<T[]>, unknown>>;
};
export const SearchResultsLayout = <T extends ListingBuyerSearchResultModel | PackageBuyerModel>({
    isLoading,
    data,
    isPackage,
    hasNextPage,
    total,
    fetchNextPage,
    isFetchingNextPage,
}: SearchResultsLayoutProps<T>) => {
    const searchFormData = useSearchFormDataContext().getSearchFormData();
    const navigate = useNavigate();
    const { sortCriteria, setSortCriteria, setScrollPosition, scrollPosition } = useSearchResultsContext();
    const isAlertFeatureSupported = useIsFeatureSupported(Feature.ALERT);

    const { PurchaseModals, isBuyingListingId, handleBuyNow, handleAddToCart, listingIdAddingToCart } =
        useBuyNowAndAddToCart({
            isPackage: isPackage || false,
            checkoutPath: isPackage ? RoutePath.PACKAGE_SEARCH_CHECKOUT : RoutePath.SEARCH_LISTINGS_CHECKOUT,
            checkoutBackPath: isPackage ? RoutePath.PACKAGE_SEARCH_RESULTS : RoutePath.SEARCH_LISTINGS_RESULTS,
        });

    const handleBackToSearch = (): void => {
        navigate(RoutePath.SEARCH_LISTINGS);
    };

    useEffect(() => {
        scrollMainLayout(0, scrollPosition);
        setScrollPosition(0);
    }, []);
    return (
        <div className="listing-search-results">
            <SearchResultsPageHeader handleBackToSearch={handleBackToSearch} />
            <TableLayout>
                <ListingSearchSummary isPackage={isPackage} />
                <Separator separatorType={SeparatorIdentifier.ON_LIGHT} />
                {!isPackage && isAlertFeatureSupported && <ListingAlertNotification />}

                {/* eslint-disable-next-line no-nested-ternary */}
                {isLoading ? (
                    <LoadingSpinner />
                ) : data?.length ? (
                    <SearchResults<T>
                        searchFormData={searchFormData}
                        totalResults={total}
                        results={data}
                        hasNext={hasNextPage}
                        handleLoadMore={fetchNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        sortCriteria={sortCriteria}
                        handleSortChange={setSortCriteria}
                        pathToListingDetails={(listingId) => {
                            const route = isPackage
                                ? RoutePath.PACKAGE_SEARCH_DETAILS
                                : RoutePath.SEARCH_LISTING_DETAILS;
                            const routeParams = isPackage ? 'packageId' : 'listingId';
                            return createRouteUrl(route, [routeParams, listingId]);
                        }}
                        onClickListing={() => {
                            setScrollPosition(getScrollTopMainLayout());
                        }}
                        isPackage={isPackage}
                        handleCheckout={handleBuyNow}
                        onClickCartButton={handleAddToCart}
                        selectedListingId={listingIdAddingToCart}
                        isBuyingListingId={isBuyingListingId}
                    />
                ) : (
                    <SearchResultsEmptyState handleBackToSearch={handleBackToSearch} />
                )}
                <PurchaseModals />
            </TableLayout>
        </div>
    );
};
